import * as React from "react";
import "./CustomerListPage.scss";
import Api from "./Api";
import Employee from "./Employee";
import SelectCtl from "./input/SelectCtl";
import Dialog from "./input/Dialog";
import Segment from "@testout/testout-commerce/models/Segment";
import { Territory } from "@testout/testout-commerce/models/commerce/Territory";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { PagedList } from "@testout/testout-commerce/models/PagedList";
import { ICustomer, Customer } from "@testout/testout-commerce/models/commerce/Customer";
import FilterCtl from "./input/FilterCtl";
import { SelectViewCtl, IEmployeeView2 } from "./SelectViewCtl";

interface CustomerListPageProps { }

interface CustomerListPageState {
    errorMessage: string;
    searchInProgress: boolean;
    inProgress: string;
    customers: Customer[];
    defaultView: IEmployeeView2
    view: IEmployeeView2;
    page: number;
    rowsPerPage: number;
    totalCount: number;
    dialogTitle?: string;
    dialogUrl?: string;
    filterDefaultItems: { [name: string]: ListItem };
    employees: ListItem[];
}

export default class CustomerListPage extends React.PureComponent<CustomerListPageProps, CustomerListPageState> {

    constructor(props: CustomerListPageProps) {
        super(props);
        const defaultView = {
            name: "My Territory Customers",
            type: "Customer",
            employeeNumber: Employee.current?.employeeNumber ?? ""
        };
        this.state = {
            inProgress: "",
            defaultView: defaultView, view: { ...defaultView },
            errorMessage: "", searchInProgress: false, customers: [], employees: [],
            page: 0, rowsPerPage: 25, totalCount: 0, filterDefaultItems: {}
        };
    }

    _searchRef = React.createRef<HTMLInputElement>();

    _rowsPerPageList: ListItem[] = [
        { name: "25 per page", value: "25" },
        { name: "50 per page", value: "50" },
        { name: "100 per page", value: "100" }
    ];

    async componentDidMount() {
        window.addEventListener("message", this.closeWindow);
        await this.initFilters();
        this.onRefreshClick();

        const divSearch = this._searchRef.current;
        if (divSearch)
            divSearch.focus();
    }

    componentWillUnmount() {
        window.removeEventListener("message", this.closeWindow);
    }

    closeWindow = () => {
        this.setState({ dialogTitle: "", dialogUrl: "" });
    }

    private async initFilters() {
        try {
            // Save the employee list for lookups
            let employees = await Api.get<ListItem[]>('/api/employees/list-items');
            this.setState({ employees: employees });

            let filterDefaultItems: { [name: string]: ListItem } = {};

            // Get the default salesperson
            let employeeNumber = Employee.current?.employeeNumber;
            filterDefaultItems["SalespersonCode"] = {
                name: "Me", value: employeeNumber,
                description: "The current user"
            };

            // Get the default territories
            const territories = await Api.get<Territory[]>(`/api/employees/${employeeNumber}/territories`);
            const territoryCodes: string | null = territories && territories.length ? territories.map(t => t.territoryCode).join(",") : null;
            if (territoryCodes)
                filterDefaultItems["TerritoryCode"] = {
                    name: "My Territory", value: territoryCodes,
                    description: "The current user's territories"
                };

            // Set the default view
            let defaultView: IEmployeeView2;
            defaultView = {
                ...this.state.defaultView,
                name: "My Territory Customers",
                filters: territoryCodes ? { "TerritoryCode": territoryCodes.split(',') } : { }
            };

            this.setState({ filterDefaultItems: filterDefaultItems, defaultView: defaultView, view: { ...defaultView } });
        }
        catch (e: any) {
            this.setState({ errorMessage: e.message });
        }
    }

    onFilterSelect = async (name: string, value?: string): Promise<ListItem[]> => {
        let list: ListItem[];
        switch (name) {
            case "SalespersonCode":
                list = value
                    ? [...this.state.employees.filter(e => e.name.toLowerCase().includes(value.toLowerCase()) ||
                        e.value?.toLowerCase().includes(value.toLowerCase()) || e.description?.toLowerCase().includes(value.toLowerCase()))]
                    : [...this.state.employees];
                if (this.state.filterDefaultItems[name])
                    list.unshift(this.state.filterDefaultItems[name]);
                return list;
            case "TerritoryCode":
                list = value
                    ? await Api.get<ListItem[]>(`/api/territories/list-items?search=${encodeURIComponent(value)}`)
                    : await Api.get<ListItem[]>(`/api/territories/list-items`);
                if (this.state.filterDefaultItems[name])
                    list.unshift(this.state.filterDefaultItems[name]);
                return list;
            case "Segment":
                list = Segment.items;
                return value ? list.filter(item => item.name.toLowerCase().includes(value.toLowerCase())) : list;
            default:
                throw new Error(`Filter ${name} is not available.`);
        }
    }

    onFilterChange = (name: string, values: string[]) => {
        let expandedValues: string[] = [];

        // Expand any compound values (separated with comma)
        for (const value of values)
            if (value.includes(",")) {
                for (const subValue in value.split(","))
                    if (!expandedValues.includes(subValue))
                        expandedValues.push(subValue);
            }
            else if (!expandedValues.includes(value))
                expandedValues.push(value);

        this.setState({ view: { ...this.state.view, filters: { ...this.state.view.filters, [name]: expandedValues } } }, this.onRefreshClick);
    };

    onFilterClear = () => this.setState({ view: { ...this.state.view, filters: {} } }, this.onRefreshClick);

    onSearchChange = (value: string) => this.setState({ view: { ...this.state.view, search: value } }, this.onRefreshClick);

    onViewChange = (view: IEmployeeView2) => this.setState({ view: { ...view } }, this.onRefreshClick);

    onPageChange = (value: number) => this.setState({ page: value }, this.onRefreshClick);

    onRowsPerPageChange = (value: string) => this.setState({ page: 0, rowsPerPage: parseInt(value) }, this.onRefreshClick);

    private _debounceTimer: number | undefined = undefined;
    onRefreshClick = async () => {
        // Limit refresh interval to minimum of 300ms so we don't overwhelm the server
        if (this._debounceTimer)
            window.clearTimeout(this._debounceTimer);
        this._debounceTimer = window.setTimeout(async () => {
            this._debounceTimer = undefined;

            const { view, page, rowsPerPage } = this.state;
            if ((view.search ?? "").length === 0 || (view.search ?? "").length >= 3) {

                // Retrieve the list of orders using the search and filter values
                this.setState({ searchInProgress: true, errorMessage: "" });
                try {
                    let list = await Api.get<PagedList<Customer>>(`/api/customers/list?search=${encodeURIComponent(view.search ?? "")}&filters=${encodeURIComponent(JSON.stringify(view.filters ?? {}))}&page=${page}&size=${rowsPerPage}`);
                    this.setState({
                        customers: list?.items?.map((customer: Customer) => new Customer(customer)) ?? [],
                        totalCount: list?.totalCount ?? 0
                    });
                }
                catch (error: any) {
                    this.setState({ errorMessage: `Error while retrieving the customer list: ${error.message}` });
                }
                finally {
                    this.setState({ searchInProgress: false });
                }
            }
        }, 300);
    }

    getPossiblePageNumbers = (): number[] => {
        let startPage = this.state.page - 5;
        if (startPage < 0)
            startPage = 0;
        let array = new Array<number>();
        for (let i = 0; i < 11; i++) {
            array.push(startPage + i);
            if ((startPage + i + 1) * this.state.rowsPerPage >= this.state.totalCount)
                break;
        }
        return array;
    }

    onCreateClick = () => {
        this.setState({ dialogTitle: "Create Customer", dialogUrl: "/Customer" })
    }

    onExportClick = async () => {
        try {
            const { view, totalCount } = this.state;
            this.setState({ inProgress: `Exporting ${totalCount.toLocaleString()} records...` });
            await Api.get<PagedList<ICustomer>>(`/api/customers/list?search=${encodeURIComponent(view.search ?? "")}&filters=${encodeURIComponent(JSON.stringify(view.filters ?? {}))}&page=-1`);
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while exporting list: ${error.message}` });
        }
        finally {
            this.setState({ inProgress: "" });
        }
    }

    render() {
        const { defaultView, view, page, rowsPerPage, totalCount } = this.state;

        return <div className="customerListPage">
            <div className="title">
                <div>
                    <SelectViewCtl defaultView={defaultView} view={view} onChange={this.onViewChange} />
                    <div className="actions">
                        <button className="primary" title="Create Customer" onClick={this.onCreateClick}>Create Customer</button>
                    </div>
                </div>
                <h6>{totalCount.toLocaleString()} records</h6>
                
            </div>

            <div className="filters">
                <FilterCtl name="SalespersonCode" label="Owner" values={view.filters?.["SalespersonCode"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="TerritoryCode" label="Territory" values={view.filters?.["TerritoryCode"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="Segment" label="Segment" values={view.filters?.["Segment"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                {Object.keys(view.filters ?? {}).find((name: string) => !!view.filters?.[name]?.length) &&
                    <button title="Clear All" className="clear-all" onClick={this.onFilterClear}>Clear All</button>}
            </div>

            <div className="searchDiv">
                <div className="search">
                    <input type="text" ref={this._searchRef}
                        placeholder="Search for customer number, name, or email"
                        value={view.search}
                        onChange={(e) => this.onSearchChange(e.target.value)} />
                    <button className={`search${this.state.searchInProgress ? " spinner" : ""}`}
                        title="Search/Refresh"
                            onClick={this.onRefreshClick}
                            disabled={this.state.searchInProgress}></button>
                </div>
                <div className="actions">
                    <a className="tertiary button" title="Export" href={`/api/customers/export?search=${view.search ?? ""}&filters=${encodeURIComponent(JSON.stringify(view.filters ?? "{}"))}`}>Export</a>
                </div>
            </div>
            {this.state.errorMessage &&
                <p className="error">{this.state.errorMessage}</p>}
            <div className="scrollableDiv">
                {this.state.searchInProgress &&
                    <div className="saving"><div><div>Searching...</div><div className="spinner"></div></div></div>}
                <table>
                    <thead>
                        <tr className={this.state.customers.length === 0 ? "empty" : undefined}>
                            <th>CUSTOMER NUMBER</th>
                            <th>CUSTOMER NAME</th>
                            <th>LOCATION</th>
                            <th>PRIMARY CONTACT</th>
                            <th>SALESPERSON</th>
                            <th>PARENT CUSTOMER</th>
                            <th>SEGMENT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.customers.length === 0 && <tr className="empty" key="empty"><td colSpan={7}>No customers match the current search text and filters.</td></tr>}
                        {this.state.customers.map((customer: Customer) => <tr key={customer.customerNumber}>
                            <td><button className="link" tabIndex={0} onClick={() => this.setState({
                                dialogTitle: `${customer.customerNumber}: ${customer.name} - View/Edit Customer`,
                                dialogUrl: `/Customer/${customer.customerNumber}`
                            })}>{customer.customerNumber}</button></td>
                            <td>{customer.name}</td>
                            <td>{customer.address.postal.toCityString()}</td>
                            <td>{customer.address.contactPerson.toString()}</td>
                            <td>{this.state.employees.find(e => e.value === customer.salespersonCode)?.name ?? customer.salespersonCode}</td>
                            <td>{customer.parentCustomerNumber}</td>
                            <td>{Segment.items.find(s => s.value === customer.segment)?.name}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
            <div className="pagination">
                <button className="pageButton" disabled={page === 0} onClick={() => this.onPageChange(page - 1)}><span style={{ fontSize: "large" }}>&#10094;</span>&nbsp;&nbsp;Prev</button>
                {this.getPossiblePageNumbers().map((i: number) => 
                    <button key={i} className={`pageButton ${i === page ? "currentPage" : ""}`}
                            onClick={() => this.onPageChange(i)}>{i + 1}</button>
                )}
                <button className="pageButton" disabled={(page + 1) * this.state.rowsPerPage >= this.state.totalCount}
                    onClick={() => this.onPageChange(page + 1)}>Next&nbsp;&nbsp;<span style={{ fontSize: "large" }}>&#10095;</span></button>
                <SelectCtl className="page-size" value={rowsPerPage.toString()} onChange={this.onRowsPerPageChange} hideItemValue={true} items={this._rowsPerPageList} />
            </div>

            {this.state.inProgress !== "" &&
                <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}

            <Dialog isOpen={!!this.state.dialogUrl} title={this.state.dialogTitle || ""} onClose={() => this.setState({ dialogTitle: "", dialogUrl: "" })}>
                <iframe src={this.state.dialogUrl} title="dialog" frameBorder="none" className="customer"></iframe>
            </Dialog>

        </div>;
    }
}